import { addDoc, collection, doc, serverTimestamp, updateDoc, type Firestore } from 'firebase/firestore';
export type ChangeOrderStatus =
  | 'requested'
  | 'reviewed'
  | 'approved'
  | 'assigned'
  | 'in_progress'
  | 'done'
  | 'customer_confirmed'
  | 'closed'
  | 'rejected'
  | 'on_hold';

export type ChangeOrderPriority = 'low' | 'medium' | 'high' | 'urgent';
export type ChangeOrderCustomerApproval = 'pending' | 'approved' | 'rejected';

export const CHANGE_ORDER_STATUS_OPTIONS: Array<{ value: ChangeOrderStatus; label: string }> = [
  { value: 'requested', label: 'Requested' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'approved', label: 'Approved' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Completed' },
  { value: 'customer_confirmed', label: 'Customer Confirmed' },
  { value: 'closed', label: 'Closed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'on_hold', label: 'On Hold' },
];

export const CHANGE_ORDER_PRIORITY_OPTIONS: Array<{ value: ChangeOrderPriority; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export const CHANGE_ORDER_CUSTOMER_APPROVAL_OPTIONS: Array<{
  value: ChangeOrderCustomerApproval;
  label: string;
}> = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export const DEFAULT_CHANGE_ORDER_AGENCIES = [
  'Plumber',
  'Stone & Flooring',
  'Electrician',
  'Carpenter',
  'Painter',
  'Aluminium',
  'Civil',
];

export const DEFAULT_CHANGE_ORDER_CATEGORY_AGENCY_MAP: Record<string, string> = {
  plumbing: 'Plumber',
  wc_shift: 'Plumber',
  basin_shift: 'Plumber',
  granite_change: 'Stone & Flooring',
  tile_change: 'Stone & Flooring',
  flooring: 'Stone & Flooring',
  electrical: 'Electrician',
  switchboard: 'Electrician',
  carpentry: 'Carpenter',
  painting: 'Painter',
  civil: 'Civil',
};

export function normalizeChangeOrderCategory(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function getDefaultAgencyForCategory(category: unknown): string {
  const key = normalizeChangeOrderCategory(category);
  return DEFAULT_CHANGE_ORDER_CATEGORY_AGENCY_MAP[key] || '';
}

export function isOpenChangeOrderStatus(status: unknown): boolean {
  const normalized = String(status || '').trim().toLowerCase();
  return !['done', 'closed', 'rejected', 'customer_confirmed'].includes(normalized);
}

export function buildChangeOrderNo(now: Date = new Date()): string {
  const suffix = String(now.getTime()).slice(-5);
  return `CO-${suffix}`;
}

const statusText = (v: string) => CHANGE_ORDER_STATUS_OPTIONS.find((o) => o.value === v)?.label || v;
const COMPLETED = new Set(['done', 'closed', 'customer_confirmed']);

/**
 * Change a change order's status the way the Change Orders page does: stamps who/when, sets
 * completedAt/By the first time it is completed, and adds a status_change entry to its timeline.
 */
export async function changeChangeOrderStatus(db: Firestore, projectId: string, changeOrderId: string, fromStatus: string, toStatus: ChangeOrderStatus, email: string) {
  if (fromStatus === toStatus) return;
  const payload: Record<string, any> = { status: toStatus, statusUpdatedAt: serverTimestamp(), updatedAt: serverTimestamp(), updatedBy: email };
  if (COMPLETED.has(toStatus) && !COMPLETED.has(fromStatus)) {
    payload.completedAt = serverTimestamp();
    payload.completedBy = email;
  }
  await updateDoc(doc(db, 'projects', projectId, 'changeOrders', changeOrderId), payload);
  await addDoc(collection(db, 'projects', projectId, 'changeOrders', changeOrderId, 'timeline'), {
    type: 'status_change',
    message: `Status changed from ${statusText(fromStatus)} to ${statusText(toStatus)}.`,
    fromStatus,
    toStatus,
    createdBy: email,
    createdAt: serverTimestamp(),
  });
}

/** The usual next step for a change order (main button), and the other moves offered beside it. */
export function nextChangeOrderSteps(status: string): { main: { to: ChangeOrderStatus; label: string } | null; more: Array<{ to: ChangeOrderStatus; label: string }> } {
  switch (status) {
    case 'requested':
    case 'reviewed':
      return { main: { to: 'approved', label: 'Approve' }, more: [{ to: 'on_hold', label: 'Put on hold' }, { to: 'rejected', label: 'Reject' }] };
    case 'approved':
    case 'assigned':
      return { main: { to: 'in_progress', label: 'Start work' }, more: [{ to: 'on_hold', label: 'Put on hold' }, { to: 'rejected', label: 'Reject' }] };
    case 'in_progress':
      return { main: { to: 'done', label: 'Mark completed' }, more: [{ to: 'on_hold', label: 'Put on hold' }] };
    case 'on_hold':
      return { main: { to: 'in_progress', label: 'Resume work' }, more: [{ to: 'rejected', label: 'Reject' }] };
    case 'done':
      return { main: { to: 'customer_confirmed', label: 'Customer confirmed' }, more: [{ to: 'closed', label: 'Close' }] };
    case 'customer_confirmed':
      return { main: { to: 'closed', label: 'Close' }, more: [] };
    default:
      return { main: null, more: [] };
  }
}
